"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Quote, ArrowRight, CircleCheck as CheckCircle2, TrendingUp, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Link } from "@/components/common/Link";
const achievements = [{
  metric: "+420%",
  label: "Organic Traffic",
  desc: "Achieved sustained month-over-month growth across core category and product pages."
}, {
  metric: "+280%",
  label: "Organic Revenue",
  desc: "Scaled high-intent non-brand commercial search visibility and direct ecommerce sales."
}, {
  metric: "+65%",
  label: "Conversion Rate",
  desc: "Optimized landing page taxonomy, crawl architecture, and collection UX."
}];
export default function Spotlight() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="clienttestimonialspotlight" data-nav="light" className="bg-background py-20 md:py-28 text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8 items-center">
          {}
          <motion.div variants={itemVariants} className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-border bg-secondary/50 text-foreground px-3 py-1 font-medium">
                <Sparkles className="size-3 text-accent mr-1" />
                Verified Client Story
              </Badge>
              <span className="text-xs font-mono text-muted-foreground uppercase tracking-wide">
                Direct Collaboration
              </span>
            </div>

            <div className="relative">
              <Quote className="absolute -top-4 -left-3 size-12 text-secondary/60 pointer-events-none -z-0" />
              <blockquote className="relative z-10 text-2xl sm:text-3xl font-serif font-medium leading-relaxed tracking-tight text-foreground">
                {"\"We knew SEO was important, but we had no idea it could drive this kind of growth. The work was strategic, measurable, and game-changing for our business.\""}
              </blockquote>
            </div>

            <div className="flex items-center gap-4 pt-2">
              <SafeHydrate><Avatar className="size-14 border border-border shadow-xs">
                <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-lg">
                  FA
                </AvatarFallback>
              </Avatar></SafeHydrate>
              <div className="flex flex-col">
                <div className="flex items-center gap-1.5">
                  <span className="font-semibold text-lg text-foreground">Fatima Asrafy</span>
                  <CheckCircle2 className="size-4 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Marketing Manager | Fashion Retailer Client
                </p>
              </div>
            </div>

            <Separator className="my-2 bg-border" />

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="shadow-xs font-medium">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </motion.div>

          {}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <Card className="border border-border bg-card/90 shadow-md">
              <CardContent className="p-6 sm:p-8 flex flex-col gap-6">
                <div className="flex items-center justify-between pb-2 border-b border-border">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="size-5 text-primary" />
                    <span className="font-semibold text-base text-card-foreground">
                      Transformation Highlights
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground font-mono">
                    Direct Impact
                  </span>
                </div>

                <div className="space-y-6">
                  {achievements.map((item, i) => <div key={item.label} data-index={i} className="flex flex-col gap-1.5 transition-[background-color,border-color] duration-200 hover:bg-muted/30 p-3 rounded-md">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-2xl sm:text-3xl font-bold font-serif text-primary">
                          {item.metric}
                        </span>
                        <span className="text-sm font-semibold text-foreground text-right">
                          {item.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>)}
                </div>

                <div className="bg-muted/40 rounded-md p-4 border border-border/60">
                  <p className="text-xs text-muted-foreground leading-normal">
                    <strong className="text-foreground font-medium">My Commitment:</strong> Direct, hands-on execution across technical crawl architecture, category taxonomy restructure, and collection page UX tailored specifically for ecommerce scale.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}